/**
 * Compact My Stuff summary - shared by section panel and legacy Basecamp sidebar widget.
 * Links to canonical Todos table (not a duplicate assignment list).
 */

import { ListTodo, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { BasecampAssignment } from '@/types/basecamp';
import { isBasecampDueOverdue, isBasecampDueToday } from '@/lib/basecamp-calendar';

export function openBasecampTodos(organizationId?: string) {
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: 'basecamp-todos',
        title: 'Todos',
        type: 'integration-basecamp',
        integrationData: {
          integrationId: 'basecamp',
          section: 'todos',
          ...(organizationId ? { organizationId } : {}),
        },
      },
    })
  );
}

export function openBasecampTriageTab() {
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: 'basecamp-triage',
        title: 'Weekly Triage',
        type: 'integration-basecamp-triage',
        integrationData: { integrationId: 'basecamp' },
      },
    })
  );
}

export function getMyStuffCounts(assignments: BasecampAssignment[] | undefined) {
  const list = assignments || [];
  return {
    total: list.length,
    overdue: list.filter((a) => isBasecampDueOverdue(a.due_on)).length,
    dueToday: list.filter((a) => isBasecampDueToday(a.due_on)).length,
  };
}

type MyStuffSummaryProps = {
  assignments: BasecampAssignment[] | undefined;
  isLoading: boolean;
  error: string | null;
  cachedAt?: string | null;
  organizationId?: string;
  onRefresh?: () => void;
  /** Sidebar widget: tighter padding */
  variant?: 'panel' | 'widget';
};

export function MyStuffSummary({
  assignments,
  isLoading,
  error,
  cachedAt,
  organizationId,
  onRefresh,
  variant = 'panel',
}: MyStuffSummaryProps) {
  const { total, overdue, dueToday } = getMyStuffCounts(assignments);
  const isWidget = variant === 'widget';

  if (isLoading && !assignments?.length) {
    return (
      <div
        className={
          isWidget
            ? 'flex items-center justify-center py-6 text-xs text-muted-foreground'
            : 'flex items-center justify-center py-8 text-sm text-muted-foreground'
        }
        data-testid="my-stuff-summary-loading"
      >
        <RefreshCw className="w-3.5 h-3.5 animate-spin mr-2" />
        Loading assignments...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center px-3 py-4" data-testid="my-stuff-summary-error">
        <p className="text-xs text-red-500 mb-2">Failed to load assignments</p>
        {onRefresh && (
          <Button variant="outline" size="sm" onClick={onRefresh} className="text-xs">
            Try again
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className={isWidget ? 'px-3 py-3 text-center' : 'text-center max-w-sm mx-auto px-6'}
      data-testid="my-stuff-summary"
    >
      <p className={`text-muted-foreground mb-3 ${isWidget ? 'text-xs' : 'text-sm'}`}>
        {overdue} overdue � {dueToday} due today � {total} total
      </p>
      <div className={`flex flex-wrap justify-center gap-2 ${isWidget ? '' : 'mb-0'}`}>
        <Button
          variant="default"
          size={isWidget ? 'sm' : 'default'}
          onClick={() => openBasecampTodos(organizationId)}
          className="gap-2"
          data-testid="my-stuff-open-todos"
        >
          <ListTodo className="w-4 h-4" />
          View todos
        </Button>
        {isWidget && (
          <Button
            variant="outline"
            size="sm"
            onClick={openBasecampTriageTab}
            className="gap-1 text-xs"
            data-testid="my-stuff-open-triage"
          >
            Weekly triage
          </Button>
        )}
      </div>
      {cachedAt && (
        <p className={`text-muted-foreground mt-3 ${isWidget ? 'text-[10px]' : 'text-xs'}`}>
          Last sync: {new Date(cachedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
}
